/**
 * @file interpret/components/InterpretTranscript.tsx
 * @brief The live conversation, in the same bubbles as Session details.
 *
 * @details
 * Sits where the Boardroom recorder shows its agenda (right column on desktop,
 * under the controls on phones). Person A on the left in brand, Person B on the
 * right in grey (`TranscriptBubbleTurn`), original text then translation, the
 * "Urdu → English" meta only when the spoken language changes, as the phone's
 * transcript sheet does. Follows the newest turn unless the user scrolled up.
 *
 * 01-10-2026 (DES): the newest turn always sits well clear of the window's
 * bottom edge. A tall end spacer is what `scrollIntoView` lands on, and the
 * follow also fires when the last turn's translation arrives and grows it.
 *
 * Each bubble leads its bottom break with the phone's play button
 * (`SessionTranscriptBubbleRow`): a 22px disc that replays the turn's
 * translation, a Square while it plays, dimmed unless the session is paused.
 * Pressing it while live says why instead of doing nothing.
 *
 * Calm view (DES 19-10-2026): while the session is live (`focus`), only the
 * newest turn shows, large: what was said, then its translation, not a
 * scrolling wall. "Show conversation" opens the full list; pausing shows it
 * too, since replay lives on its bubbles.
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useRef, useState } from "react";
import { Play, Square } from "lucide-react";
import { TranscriptBubbleTurn } from "@/components/ui/TranscriptBubbleTurn";
import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";
import { getLanguageEnglishName, languageBaseCode } from "@/shared/utils/languages";
import { speakerInitials } from "@/features/sessions/components/SessionDetailDialog";
import type { InterpretTurn } from "../engine/interpretController";

type Props = {
  turns: InterpretTurn[];
  personAName: string;
  personBName: string;
  personAAvatar?: string | null;
  personAGender?: string | null;
  personBGender?: string | null;
  /** Replay is allowed (the session is paused). */
  playbackEnabled: boolean;
  /** The turn being replayed: its button shows Stop. */
  replayingId: string | null;
  hasAudio: (turnId: string) => boolean;
  onPlay: (turnId: string) => void;
  /** The screen's hidden controls, in the tips icon by the heading (the hint
   *  icon standard). None = no icon. */
  tips?: string[];
  /** Live: show only the newest turn, large (calm view, 19-10-2026). */
  focus?: boolean;
};

export function InterpretTranscript({
  turns,
  personAName,
  personBName,
  personAAvatar,
  personAGender,
  personBGender,
  playbackEnabled,
  replayingId,
  hasAudio,
  onPlay,
  tips,
  focus = false,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const calm = focus && !showAll;
  const hint = useHintPopover("interpret_tips");
  const endRef = useRef<HTMLDivElement>(null);
  const stuckRef = useRef(true);

  useEffect(() => {
    const onScroll = () => {
      const el = endRef.current;
      if (!el) return;
      stuckRef.current = el.getBoundingClientRect().top < window.innerHeight + 80;
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, []);

  const last = turns[turns.length - 1];
  useEffect(() => {
    if (stuckRef.current) endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [turns.length, last?.srcText, last?.tgtText]);

  return (
    <section className="w-full space-y-3" aria-label="Live transcript">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">
          Conversation{turns.length > 0 ? ` (${turns.length})` : ""}
        </h2>
        {tips?.length ? (
          <HintIcon open={hint.open} onClick={hint.toggle} anchorRef={hint.anchorRef} />
        ) : null}
      </div>
      {tips?.length && hint.open ? (
        <HintBubble lines={tips} onClose={hint.close} anchorRef={hint.anchorRef} />
      ) : null}
      {turns.length === 0 ? (
        <p className="text-[13px] leading-[18px] text-muted-foreground">
          Each turn appears here with its translation as you speak.
        </p>
      ) : calm && last ? (
        // The newest turn only, large (calm view).
        <div aria-live="polite" className="pt-2">
          <p className="mb-2 text-[13px] font-semibold text-muted-foreground">
            {last.speaker === "A" ? personAName : personBName}
          </p>
          <p
            dir={isRTLText(last.srcText) ? "rtl" : "ltr"}
            className="line-clamp-3 whitespace-pre-line break-words text-[26px] font-semibold leading-[1.35] text-app-text"
          >
            {last.srcText}
          </p>
          {last.tgtText ? (
            <p
              dir={isRTLText(last.tgtText) ? "rtl" : "ltr"}
              className="mt-3 line-clamp-3 whitespace-pre-line break-words text-[22px] leading-[1.4] text-app-text-secondary"
            >
              {last.tgtText}
            </p>
          ) : null}
          {turns.length > 1 ? (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Show conversation
            </button>
          ) : null}
        </div>
      ) : (
        <ol className="m-0 flex list-none flex-col gap-2 p-0" aria-live="polite">
          {turns.map((t, i) => {
            const isA = t.speaker === "A";
            const name = isA ? personAName : personBName;
            const prev = turns[i - 1];
            const langChanged =
              !prev || languageBaseCode(prev.srcLang) !== languageBaseCode(t.srcLang);
            const meta = langChanged
              ? `${getLanguageEnglishName(t.srcLang)} → ${getLanguageEnglishName(t.tgtLang)}`
              : null;
            const playing = replayingId === t.id;
            return (
              <TranscriptBubbleTurn
                key={t.id}
                isA={isA}
                name={name}
                avatarUrl={isA ? personAAvatar : null}
                initials={speakerInitials(name)}
                gender={isA ? personAGender : personBGender}
                meta={meta}
                bottomAccessory={
                  hasAudio(t.id) ? (
                    <button
                      type="button"
                      onClick={() => onPlay(t.id)}
                      aria-label={
                        !playbackEnabled
                          ? "Playback available when the session is paused"
                          : playing
                            ? "Stop"
                            : "Play this turn"
                      }
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition-opacity",
                        !playbackEnabled && "opacity-25",
                      )}
                    >
                      {playing ? (
                        <Square className="h-3 w-3 fill-current" aria-hidden />
                      ) : (
                        <Play
                          className="ml-0.5 h-3.5 w-3.5 fill-current"
                          strokeLinejoin="round"
                          aria-hidden
                        />
                      )}
                    </button>
                  ) : undefined
                }
              >
                <p
                  dir={isRTLText(t.srcText) ? "rtl" : "ltr"}
                  className="whitespace-pre-line break-words text-[17px] leading-[1.45] text-app-text"
                >
                  {t.srcText}
                </p>
                {t.tgtText ? (
                  <p
                    dir={isRTLText(t.tgtText) ? "rtl" : "ltr"}
                    className="mt-2 whitespace-pre-line break-words text-[15px] leading-relaxed text-app-text-secondary opacity-70"
                  >
                    {t.tgtText}
                  </p>
                ) : null}
              </TranscriptBubbleTurn>
            );
          })}
        </ol>
      )}
      {focus && showAll ? (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Show the latest only
        </button>
      ) : null}
      {/* The gap kept under the newest turn when following. */}
      <div ref={endRef} aria-hidden className="h-[clamp(6rem,22vh,12rem)]" />
    </section>
  );
}
